import React, { Component } from 'react';
import clsx from 'clsx';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withStyles } from '@mui/styles';
import {
  Container, Button, IconButton, Skeleton, CircularProgress, Tooltip, Autocomplete, Switch, FormControlLabel,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, InputAdornment,
} from '@mui/material';
import {
  Phone, PhoneCall, Settings, Plus, Pencil, Trash2, Check, Minus, X, ExternalLink,
  Globe, Monitor, Eye, EyeOff, Info, Users, Search, ChevronLeft, ChevronRight, KeyRound,
  Copy, RefreshCw, Link2, PhoneForwarded,
} from 'lucide-react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/material.css';
import { injectIntl } from 'react-intl';
import { Styles } from '../../styles/material-styles';
import { Overlay } from '../../styles/global';
import HeaderMenu from '../../components/header-menu';
import Footer from '../../components/footer';
import CustomizedSnackbars from '../../components/material-snackbars';
import Api from '../../services/api';
import * as voipService from '../../services/voip.service';
import VoipCallsHistory from './components/voip-calls-history';
import * as componentActions from '../../redux/actions/header-menu';

const GREEN = 'var(--mexx-green-primary, #8BC34A)';
const BLUE = '#277afe';
const EXT_PAGE_SIZE = 5;

const MODALITY = {
  embedded_webrtc: { label: 'Discador WebRTC', short: 'WebRTC', color: GREEN, Icon: Globe, desc: 'Discador completo dentro do navegador, sem instalar nada na máquina do agente.' },
  external_softphone: { label: 'Softphone externo', short: 'Softphone', color: BLUE, Icon: Monitor, desc: 'O áudio fica no app/softphone do provedor instalado na máquina do agente. O Mexx dispara, monitora e registra a chamada via API.' },
  click_to_call: { label: 'Click-to-call', short: 'Click-to-call', color: '#f59e0b', Icon: Phone, desc: 'O Mexx origina a ligação via API; o provedor toca o ramal/telefone do agente.' },
};

const CAP_ROWS = [
  { key: 'has_recording', label: 'Gravação de chamadas' },
  { key: 'has_transcription', label: 'Transcrição automática' },
  { key: 'supports_extension_provisioning', label: 'Provisiona ramal automaticamente' },
  { key: 'supports_click_to_call', label: 'Click-to-call via API' },
  { key: 'supports_webrtc', label: 'Discador WebRTC embutido' },
];

const S = {
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))', gap: 20, marginTop: 20, alignItems: 'stretch' },
  card: { display: 'flex', flexDirection: 'column', height: '100%', border: '1px solid #e6e8eb', borderRadius: 14, background: '#fff', boxShadow: '0 1px 3px rgba(16,24,40,.06)', overflow: 'hidden', transition: 'box-shadow .2s, transform .2s' },
  cardHead: { display: 'flex', alignItems: 'flex-start', gap: 14, padding: '18px 18px 12px' },
  logo: { width: 48, height: 48, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 18, flexShrink: 0 },
  statusDot: (on) => ({ width: 8, height: 8, borderRadius: '50%', background: on ? GREEN : '#cbd5e1', display: 'inline-block', marginRight: 6 }),
  pill: (color) => ({ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11.5, fontWeight: 600, color, background: `${color}14`, border: `1px solid ${color}33`, borderRadius: 20, padding: '2px 9px', marginRight: 6, marginTop: 6 }),
  capList: { padding: '4px 18px 14px' },
  capRow: (on) => ({ display: 'flex', alignItems: 'center', gap: 9, padding: '4px 0', fontSize: 13.5, color: on ? '#1f2937' : '#9aa3af' }),
  foot: { marginTop: 'auto', display: 'flex', alignItems: 'center', gap: 8, padding: '12px 18px', borderTop: '1px solid #f1f3f5', background: '#fafbfc' },
  empty: { textAlign: 'center', padding: '64px 16px', color: '#888' },
  hint: { fontSize: 12, color: '#8a94a6', marginTop: 4 },
  sectionLabel: { fontSize: 12, fontWeight: 700, letterSpacing: .4, textTransform: 'uppercase', color: '#94a3b8', margin: '18px 0 4px' },
  modCard: (sel) => ({ border: `1.5px solid ${sel ? GREEN : '#e2e8f0'}`, background: sel ? `${GREEN}0d` : '#fff', borderRadius: 10, padding: '12px 14px', cursor: 'pointer', display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: 10, transition: 'all .15s' }),
  extRow: { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 4px', borderBottom: '1px solid #f1f3f5' },
  avatar: { width: 30, height: 30, borderRadius: '50%', background: '#e8eef9', color: BLUE, fontSize: 12.5, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  testBox: { border: '1px solid #eef1f4', borderRadius: 10, padding: '4px 12px', marginTop: 4, background: '#fbfcfd' },
  testRow: { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0' },
  testSep: { borderTop: '1px solid #f1f3f5' },
};

const initial = (name) => (name || '?').trim().charAt(0).toUpperCase();
const logoColor = (slug) => ({ nvoip: '#6d28d9', '3cx': '#0e7490', iungo: '#be123c' }[slug] || '#475569');
const firstError = (res, fallback) => (res?.errors && res.errors[0]) || fallback;
// Aplica/remove o DDI do país conforme o switch "Usar DDI".
const applyDdi = (number, country, useDdi) => {
  const n = String(number || '');
  if (useDdi) return n;
  const dc = country && country.dialCode ? String(country.dialCode) : '';
  return dc && n.startsWith(dc) ? n.slice(dc.length) : n;
};

class VoipPlatformList extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true, actionLoading: false,
      providers: [], configs: [],
      configDialogOpen: false, selectedProvider: null, editingConfig: null, formValues: {}, formModality: '', formAuthMethod: '', secretVisible: {},
      testConn: { status: 'idle', msg: '' }, testCallNumber: '', testCallCountry: null, testUseDdi: true, testCall: { status: 'idle', msg: '' }, testReceive: { status: 'idle', msg: '' },
      // extensions modal
      extModalOpen: false, extModalProvider: null, extModalConfig: null, extModalExtensions: [], extLoading: false, extSearch: '', extPage: 1,
      // add/edit extension dialog
      extDialogOpen: false, editingExt: null, extForm: { userId: '', extension: '', sipPassword: '' }, technicians: [], techLoading: false,
      // ligar (per-agente)
      callDialogOpen: false, callExtension: null, callNumber: '', callCountry: null, callUseDdi: true, callStatus: { status: 'idle', msg: '' },
      openNotification: false, notificationVariant: 'success', notificationMessage: '',
    };
    this.closeNotification = this.closeNotification.bind(this);
  }

  componentDidMount() { this.loadData(); }

  loadData = () => {
    this.setState({ loading: true });
    Promise.all([voipService.getProviders(), voipService.getConfigs()])
      .then(([pRes, cRes]) => {
        const providers = pRes?.success ? (pRes.data || []).map(voipService.normalizeProvider) : [];
        const configs = cRes?.success ? (cRes.data || []).map(voipService.normalizeConfig) : [];
        this.setState({ loading: false, providers, configs });
      })
      .catch(err => {
        this.setState({ loading: false });
        this.notify('error', err?.response?.data?.errors?.[0] || 'Erro ao carregar as plataformas.');
        Api.kickoff(err);
      });
  };

  reloadConfigs = async () => {
    const res = await voipService.getConfigs();
    if (res?.success) this.setState({ configs: (res.data || []).map(voipService.normalizeConfig) });
  };

  closeNotification() { this.setState({ openNotification: false }); }
  notify(variant, message) { this.setState({ openNotification: true, notificationVariant: variant, notificationMessage: message }); }
  configFor = (providerId) => this.state.configs.find(c => c.voipProviderId === providerId && c.isActive);

  // ---- Config dialog ----
  openConfigDialog = (provider) => {
    const existing = this.configFor(provider.id);
    const formValues = {};
    (provider.configSchema?.fields || []).forEach(f => {
      const isSecret = f.secret || f.type === 'password';
      formValues[f.key] = isSecret ? '' : (existing?.config?.[f.key] ?? f.default ?? '');
    });
    const authMethods = provider.configSchema?.authMethods || [];
    this.setState({
      configDialogOpen: true, selectedProvider: provider, editingConfig: existing || null,
      formValues, formModality: existing?.selectedModality || provider.recommendedModality || (provider.supportedModalities || [])[0],
      formAuthMethod: existing?.authMethod || provider.configSchema?.recommendedAuthMethod || (authMethods[0] && authMethods[0].value) || '',
      secretVisible: {}, webhookUrlOverride: null, regenLoading: false,
      testConn: { status: 'idle', msg: '' }, testCallNumber: '', testCallCountry: null, testUseDdi: true, testCall: { status: 'idle', msg: '' }, testReceive: { status: 'idle', msg: '' },
    });
  };
  closeConfigDialog = () => this.setState({ configDialogOpen: false, selectedProvider: null, editingConfig: null, formValues: {}, secretVisible: {}, webhookUrlOverride: null });

  // Copia a URL do webhook. Sem async (regra do babel para arrow class field).
  copyWebhookUrl = (url) => {
    navigator.clipboard.writeText(url);
    this.notify('success', this.props.intl.formatMessage({ id: 'voip.copied' }));
  };

  // Copia o número de saída (caller ID) do provedor.
  copyOutbound = (number) => {
    navigator.clipboard.writeText(String(number || ''));
    this.notify('success', this.props.intl.formatMessage({ id: 'voip.copied' }));
  };

  // Gera/regenera o token do webhook e atualiza a URL exibida. Promise chain (sem async arrow).
  regenWebhook = () => {
    const { editingConfig } = this.state;
    if (!editingConfig) return;
    this.setState({ regenLoading: true });
    voipService.regenerateWebhookToken(editingConfig.id)
      .then((res) => {
        const url = res?.data?.webhookurl ?? res?.data?.webhookUrl;
        if (res?.success && url) {
          this.setState({ webhookUrlOverride: url });
          this.notify('success', this.props.intl.formatMessage({ id: 'voip.webhook.generated' }));
        } else {
          this.notify('error', this.props.intl.formatMessage({ id: 'voip.webhook.error' }));
        }
      })
      .catch(() => this.notify('error', this.props.intl.formatMessage({ id: 'voip.webhook.error' })))
      .then(() => this.setState({ regenLoading: false }));
  };
  onFieldChange = (key, value) => this.setState(prev => ({ formValues: { ...prev.formValues, [key]: value } }));
  toggleSecret = (key) => this.setState(prev => ({ secretVisible: { ...prev.secretVisible, [key]: !prev.secretVisible[key] } }));

  saveConfig = () => {
    const { selectedProvider, editingConfig, formValues, formModality, formAuthMethod } = this.state;
    const intl = this.props.intl;
    const visible = (selectedProvider.configSchema?.fields || []).filter(f => !f.auth_method || f.auth_method === formAuthMethod);
    const missing = visible
      .filter(f => f.required && !String(formValues[f.key] || '').trim() && !(editingConfig && (f.secret || f.type === 'password')))
      .map(f => f.label);
    if (missing.length) { this.notify('error', `${intl.formatMessage({ id: 'voip.required.fields' })}: ${missing.join(', ')}`); return; }

    this.setState({ actionLoading: true });
    const payload = { selectedmodality: formModality, authmethod: formAuthMethod, values: formValues, isactive: true };
    const call = editingConfig
      ? voipService.updateConfig(editingConfig.id, payload)
      : voipService.createConfig({ voipproviderid: selectedProvider.id, ...payload });

    call.then(async res => {
      if (res?.success) { await this.reloadConfigs(); this.setState({ configDialogOpen: false, selectedProvider: null, editingConfig: null }); this.notify('success', intl.formatMessage({ id: 'voip.platform.saved' })); }
      else this.notify('error', firstError(res, intl.formatMessage({ id: 'voip.platform.saved' })));
    }).catch(err => { this.notify('error', err?.response?.data?.errors?.[0] || 'Erro ao salvar.'); Api.kickoff(err); })
      .finally(() => this.setState({ actionLoading: false }));
  };

  disableProvider = (provider) => {
    const config = this.configFor(provider.id); if (!config) return;
    this.setState({ actionLoading: true });
    voipService.deleteConfig(config.id).then(async res => {
      if (res?.success) { await this.reloadConfigs(); this.notify('success', this.props.intl.formatMessage({ id: 'voip.platform.disabled' })); }
      else this.notify('error', firstError(res, 'Erro ao desabilitar.'));
    }).catch(err => { this.notify('error', err?.response?.data?.errors?.[0] || 'Erro ao desabilitar.'); Api.kickoff(err); })
      .finally(() => this.setState({ actionLoading: false }));
  };

  // ---- Teste de conexão: REAL (valida as credenciais no provedor). Ligar/Receber seguem mock (Fase 3). ----
  runTestConnection = () => {
    const { selectedProvider, formValues, formModality, formAuthMethod } = this.state;
    this.setState({ testConn: { status: 'testing', msg: '' } });
    const payload = { voipproviderid: selectedProvider.id, selectedmodality: formModality, authmethod: formAuthMethod, values: formValues, isactive: true };
    voipService.testConnection(payload)
      .then(res => {
        if (res?.success) this.setState({ testConn: { status: 'ok', msg: '' } });
        else this.setState({ testConn: { status: 'fail', msg: firstError(res, '') } });
      })
      .catch(err => this.setState({ testConn: { status: 'fail', msg: err?.response?.data?.errors?.[0] || 'Erro ao testar.' } }));
  };
  runTestCall = () => {
    const { selectedProvider, formValues, formModality, formAuthMethod, testCallNumber, testCallCountry, testUseDdi } = this.state;
    if (!String(testCallNumber || '').trim()) { this.notify('error', this.props.intl.formatMessage({ id: 'voip.test.call.number' })); return; }
    this.setState({ testCall: { status: 'dialing', msg: '' } });
    const payload = { voipproviderid: selectedProvider.id, selectedmodality: formModality, authmethod: formAuthMethod, values: formValues, isactive: true };
    voipService.testCall(payload, applyDdi(testCallNumber, testCallCountry, testUseDdi))
      .then(res => {
        if (res?.success) this.setState({ testCall: { status: 'originated', msg: '' } });
        else this.setState({ testCall: { status: 'fail', msg: firstError(res, '') } });
      })
      .catch(err => this.setState({ testCall: { status: 'fail', msg: err?.response?.data?.errors?.[0] || 'Erro ao originar.' } }));
  };
  runTestReceive = () => {
    const { selectedProvider, formValues, formModality, formAuthMethod } = this.state;
    this.setState({ testReceive: { status: 'listening', msg: '' } });
    const payload = { voipproviderid: selectedProvider.id, selectedmodality: formModality, authmethod: formAuthMethod, values: formValues, isactive: true };
    voipService.testReceive(payload)
      .then(res => {
        if (res?.success) this.setState({ testReceive: { status: 'queried', msg: '' } });
        else this.setState({ testReceive: { status: 'fail', msg: firstError(res, '') } });
      })
      .catch(err => this.setState({ testReceive: { status: 'fail', msg: err?.response?.data?.errors?.[0] || 'Erro.' } }));
  };

  renderTestStatus(state) {
    const intl = this.props.intl;
    const map = {
      idle: { id: 'voip.test.idle', color: '#94a3b8' }, testing: { id: 'voip.test.testing', color: BLUE, spin: true },
      ok: { id: 'voip.test.ok', color: GREEN, ok: true }, fail: { id: 'voip.test.fail', color: '#dc2626', bad: true },
      dialing: { id: 'voip.call.dialing', color: BLUE, spin: true }, ringing: { id: 'voip.call.ringing', color: BLUE, spin: true },
      answered: { id: 'voip.call.answered', color: GREEN, ok: true }, ended: { id: 'voip.call.ended', color: '#64748b' },
      listening: { id: 'voip.receive.listening', color: BLUE, spin: true }, received: { id: 'voip.receive.received', color: GREEN, ok: true },
      originated: { id: 'voip.call.originated', color: GREEN, ok: true }, queried: { id: 'voip.receive.queried', color: GREEN, ok: true },
    };
    const m = map[state.status] || map.idle;
    return (
      <span style={{ display: 'inline-flex', alignItems: 'flex-start', gap: 6, fontSize: 12.5, fontWeight: 600, color: m.color, maxWidth: '100%' }}>
        <span style={{ marginTop: 1, flexShrink: 0 }}>{m.spin ? <CircularProgress size={12} thickness={6} style={{ color: m.color }} /> : m.ok ? <Check size={14} /> : m.bad ? <X size={14} /> : <Minus size={14} />}</span>
        <span style={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>{intl.formatMessage({ id: m.id })}{state.msg ? ` ${state.msg}` : ''}</span>
      </span>
    );
  }

  // ---- Extensions modal ----
  openExtModal = (provider, config) => {
    this.setState({ extModalOpen: true, extModalProvider: provider, extModalConfig: config, extModalExtensions: [], extLoading: true, extSearch: '', extPage: 1 });
    this.loadExtensions(config.id);
  };
  loadExtensions = (configId) => {
    this.setState({ extLoading: true });
    voipService.getExtensions(configId).then(res => {
      this.setState({ extLoading: false, extModalExtensions: res?.success ? (res.data || []).map(voipService.normalizeExtension) : [] });
    }).catch(err => { this.setState({ extLoading: false }); this.notify('error', err?.response?.data?.errors?.[0] || 'Erro ao carregar ramais.'); Api.kickoff(err); });
  };
  closeExtModal = () => this.setState({ extModalOpen: false, extModalProvider: null, extModalConfig: null, extModalExtensions: [] });

  // ---- Add/edit extension (carrega técnicos sob demanda ao abrir) ----
  openExtDialog = (ext = null) => {
    this.setState({ extDialogOpen: true, editingExt: ext, extForm: ext ? { userId: ext.userId, extension: ext.extension, sipPassword: '' } : { userId: '', extension: '', sipPassword: '' } });
    if (!ext) {
      this.setState({ techLoading: true, technicians: [] });
      voipService.getTechnicians().then(res => {
        this.setState({ techLoading: false, technicians: res?.success ? (res.data || []).map(voipService.normalizeTechnician) : [] });
      }).catch(err => { this.setState({ techLoading: false }); this.notify('error', err?.response?.data?.errors?.[0] || 'Erro ao carregar técnicos.'); Api.kickoff(err); });
    }
  };
  closeExtDialog = () => this.setState({ extDialogOpen: false, editingExt: null, extForm: { userId: '', extension: '', sipPassword: '' } });
  onExtFieldChange = (key, value) => this.setState(prev => ({ extForm: { ...prev.extForm, [key]: value } }));

  saveExtension = () => {
    const { editingExt, extForm, extModalConfig } = this.state;
    const intl = this.props.intl;
    if (!editingExt && !extForm.userId) { this.notify('error', intl.formatMessage({ id: 'voip.agent' })); return; }
    if (!String(extForm.extension || '').trim()) { this.notify('error', intl.formatMessage({ id: 'voip.extension.number' })); return; }

    this.setState({ actionLoading: true });
    const call = editingExt
      ? voipService.updateExtension(editingExt.id, { extension: extForm.extension, sippassword: extForm.sipPassword, isactive: true })
      : voipService.createExtension({ voipproviderconfigid: extModalConfig.id, userid: Number(extForm.userId), extension: extForm.extension, sippassword: extForm.sipPassword });

    call.then(res => {
      if (res?.success) { this.loadExtensions(extModalConfig.id); this.setState({ extDialogOpen: false, editingExt: null }); this.notify('success', intl.formatMessage({ id: 'voip.extension.saved' })); }
      else this.notify('error', firstError(res, 'Erro ao salvar ramal.'));
    }).catch(err => { this.notify('error', err?.response?.data?.errors?.[0] || 'Erro ao salvar ramal.'); Api.kickoff(err); })
      .finally(() => this.setState({ actionLoading: false }));
  };

  deleteExtension = (ext) => {
    const { extModalConfig } = this.state;
    this.setState({ actionLoading: true });
    voipService.deleteExtension(ext.id).then(res => {
      if (res?.success) { this.loadExtensions(extModalConfig.id); this.notify('success', this.props.intl.formatMessage({ id: 'voip.extension.deleted' })); }
      else this.notify('error', firstError(res, 'Erro ao remover ramal.'));
    }).catch(err => { this.notify('error', err?.response?.data?.errors?.[0] || 'Erro ao remover ramal.'); Api.kickoff(err); })
      .finally(() => this.setState({ actionLoading: false }));
  };

  // ---- Ligar (per-agente) ----
  openCallDialog = (ext) => this.setState({ callDialogOpen: true, callExtension: ext, callNumber: '', callCountry: null, callUseDdi: true, callStatus: { status: 'idle', msg: '' } });
  closeCallDialog = () => this.setState({ callDialogOpen: false, callExtension: null });
  runCall = () => {
    const { callExtension, callNumber, callCountry, callUseDdi } = this.state;
    const digits = String(callNumber || '').replace(/\D/g, '');
    if (digits.length < 12) { this.notify('error', this.props.intl.formatMessage({ id: 'voip.invalid.number' })); return; }
    this.setState({ callStatus: { status: 'dialing', msg: '' } });
    voipService.callFromExtension(callExtension.id, applyDdi(callNumber, callCountry, callUseDdi))
      .then(res => {
        if (res?.success) this.setState({ callStatus: { status: 'originated', msg: '' } });
        else this.setState({ callStatus: { status: 'fail', msg: firstError(res, '') } });
      })
      .catch(err => this.setState({ callStatus: { status: 'fail', msg: err?.response?.data?.errors?.[0] || 'Erro ao ligar.' } }));
  };

  // ---- Render ----
  renderProviderCard(provider) {
    const intl = this.props.intl;
    const config = this.configFor(provider.id);
    const enabled = !!config;
    const cap = provider.capabilities || {};
    const comingSoon = !!cap.coming_soon;
    const activeMod = config?.selectedModality;
    const cfgVals = (enabled && config && config.config) || {};
    const outboundNumber = cfgVals.outbound_caller_id || cfgVals.caller_id || cfgVals.outbound_number || cfgVals.outboundcallerid || null;

    return (
      <div key={provider.id} style={{ ...S.card, ...(comingSoon ? { opacity: 0.55, filter: 'grayscale(1)', boxShadow: 'none' } : {}) }}
        onMouseEnter={comingSoon ? undefined : (e) => { e.currentTarget.style.boxShadow = '0 6px 20px rgba(16,24,40,.12)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
        onMouseLeave={comingSoon ? undefined : (e) => { e.currentTarget.style.boxShadow = S.card.boxShadow; e.currentTarget.style.transform = 'none'; }}>

        <div style={{ ...S.cardHead, borderLeft: enabled ? `4px solid ${GREEN}` : '4px solid transparent' }}>
          <div style={{ ...S.logo, background: logoColor(provider.name) }}>{initial(provider.displayName)}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontWeight: 700, fontSize: 16, color: '#0f172a' }}>{provider.displayName}</span>
              {comingSoon ? (
                <span style={{ fontSize: 11.5, fontWeight: 700, color: '#64748b', display: 'inline-flex', alignItems: 'center', background: '#eef2f7', border: '1px solid #dbe3ec', borderRadius: 999, padding: '2px 9px', letterSpacing: 0.2 }}>
                  {intl.formatMessage({ id: 'voip.comingsoon' })}
                </span>
              ) : (
                <span style={{ fontSize: 12, fontWeight: 600, color: enabled ? '#5a8a1f' : '#94a3b8', display: 'inline-flex', alignItems: 'center' }}>
                  <span style={S.statusDot(enabled)} />{intl.formatMessage({ id: enabled ? 'voip.enabled' : 'voip.available' })}
                </span>
              )}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap' }}>
              {(provider.supportedModalities || []).map(m => {
                const meta = MODALITY[m]; if (!meta) return null; const MI = meta.Icon; const isActive = enabled && activeMod === m;
                return (
                  <Tooltip key={m} title={meta.desc}>
                    <span style={{ ...S.pill(meta.color), ...(isActive ? { background: `${meta.color}22`, boxShadow: `inset 0 0 0 1px ${meta.color}` } : {}) }}>
                      <MI size={12} /> {meta.short}{isActive ? ' ✓' : ''}
                    </span>
                  </Tooltip>
                );
              })}
            </div>
          </div>
        </div>

        <div style={S.capList}>
          {CAP_ROWS.map(row => {
            const on = !!cap[row.key];
            return (<div key={row.key} style={S.capRow(on)}>{on ? <Check size={15} color={GREEN} /> : <Minus size={15} color="#cbd5e1" />}<span>{row.label}</span></div>);
          })}
          {cap.requires_desktop_client && (
            <div style={{ ...S.capRow(true), color: '#b45309', marginTop: 2 }}>
              <Info size={15} color="#f59e0b" /><span style={{ fontSize: 12.5 }}>{intl.formatMessage({ id: 'voip.requires.desktop' })}</span>
            </div>
          )}
        </div>

        {outboundNumber && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '0 14px 10px', padding: '9px 12px', background: '#f5f8ff', border: '1px solid #dbe6ff', borderRadius: 10 }}>
            <PhoneForwarded size={15} color="#277afe" style={{ flexShrink: 0 }} />
            <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
              <span style={{ fontSize: 9.5, fontWeight: 700, color: '#7c93b8', letterSpacing: 0.5, textTransform: 'uppercase' }}>{intl.formatMessage({ id: 'voip.outbound.label' })}</span>
              <span style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{outboundNumber}</span>
            </div>
            <Tooltip title={intl.formatMessage({ id: 'voip.copy' })}>
              <IconButton size="small" onClick={() => this.copyOutbound(outboundNumber)} style={{ color: '#277afe', flexShrink: 0 }}><Copy size={15} /></IconButton>
            </Tooltip>
          </div>
        )}

        <div style={S.foot}>
          <Button variant={enabled ? 'outlined' : 'contained'} size="small" disableElevation disabled={comingSoon}
            startIcon={comingSoon ? null : (enabled ? <Settings size={16} /> : <Plus size={16} />)} onClick={() => { if (!comingSoon) this.openConfigDialog(provider); }}
            style={comingSoon ? { background: '#e2e8f0', color: '#94a3b8' } : (enabled ? { borderColor: '#cbd5e1', color: '#334155' } : { background: GREEN })}>
            {intl.formatMessage({ id: comingSoon ? 'voip.comingsoon' : (enabled ? 'voip.configure' : 'voip.enable') })}
          </Button>
          {enabled && (
            <Button variant="text" size="small" startIcon={<Users size={15} />} onClick={() => this.openExtModal(provider, config)} style={{ color: '#334155' }}>
              {intl.formatMessage({ id: 'voip.extensions' })}
            </Button>
          )}
          {enabled && (
            <Button variant="text" size="small" startIcon={<X size={15} />} onClick={() => this.disableProvider(provider)} style={{ color: '#b3261e' }}>
              {intl.formatMessage({ id: 'voip.disable' })}
            </Button>
          )}
          <span style={{ flex: 1 }} />
          {provider.documentationUrl && (
            <Tooltip title={intl.formatMessage({ id: 'voip.docs' })}>
              <IconButton size="small" component="a" href={provider.documentationUrl} target="_blank" rel="noreferrer"><ExternalLink size={16} /></IconButton>
            </Tooltip>
          )}
        </div>
      </div>
    );
  }

  renderConfigDialog() {
    const intl = this.props.intl;
    const { configDialogOpen, selectedProvider, editingConfig, formValues, formModality, formAuthMethod, secretVisible, testConn, testCallNumber, testCallCountry, testUseDdi, testCall, testReceive } = this.state;
    if (!selectedProvider) return null;
    const fields = selectedProvider.configSchema?.fields || [];
    const authMethods = selectedProvider.configSchema?.authMethods || [];
    const visibleFields = fields.filter(f => !f.auth_method || f.auth_method === formAuthMethod);
    const groups = [...new Set(visibleFields.map(f => f.group || 'credentials'))];
    const groupLabel = { credentials: intl.formatMessage({ id: 'voip.group.credentials' }), sip: intl.formatMessage({ id: 'voip.group.sip' }) };
    const mods = selectedProvider.supportedModalities || [];
    const isWebhookProvider = selectedProvider.capabilities?.event_delivery === 'webhook';
    const webhookUrl = this.state.webhookUrlOverride || (editingConfig && editingConfig.webhookUrl);

    return (
      <Dialog open={configDialogOpen} onClose={(e, reason) => { if (reason !== 'backdropClick') this.closeConfigDialog(); }} fullWidth maxWidth="sm" PaperProps={{ sx: { maxHeight: '92vh', borderRadius: 2 } }}>
        <DialogTitle style={{ display: 'flex', alignItems: 'center', gap: 12, paddingBottom: 8 }}>
          <span style={{ ...S.logo, width: 38, height: 38, fontSize: 15, background: logoColor(selectedProvider.name) }}>{initial(selectedProvider.displayName)}</span>
          <div>
            <div style={{ fontSize: 17, fontWeight: 700 }}>{`${intl.formatMessage({ id: editingConfig ? 'voip.configure' : 'voip.enable' })} ${selectedProvider.displayName}`}</div>
            <div style={{ fontSize: 12.5, color: '#94a3b8', fontWeight: 400 }}>{intl.formatMessage({ id: 'voip.config.subtitle' })}</div>
          </div>
        </DialogTitle>
        <DialogContent dividers>
          <div style={{ overflowY: 'auto', maxHeight: '64vh', paddingRight: 6 }}>
            {mods.length > 0 && (
              <>
                <div style={S.sectionLabel}>{intl.formatMessage({ id: 'voip.modality' })}</div>
                {mods.map(m => {
                  const meta = MODALITY[m]; if (!meta) return null; const MI = meta.Icon; const sel = formModality === m;
                  return (
                    <div key={m} style={S.modCard(sel)} onClick={() => this.setState({ formModality: m })}>
                      <div style={{ width: 34, height: 34, borderRadius: 8, background: `${meta.color}1a`, color: meta.color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><MI size={18} /></div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: 14, color: '#0f172a' }}>{meta.label}</div>
                        <div style={{ fontSize: 12.5, color: '#64748b', lineHeight: 1.35 }}>{meta.desc}</div>
                      </div>
                      <div style={{ width: 18, height: 18, borderRadius: '50%', border: `2px solid ${sel ? GREEN : '#cbd5e1'}`, background: sel ? GREEN : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{sel && <Check size={12} color="#fff" />}</div>
                    </div>
                  );
                })}
              </>
            )}

            {authMethods.length > 0 && (
              <>
                <div style={S.sectionLabel}>{intl.formatMessage({ id: 'voip.authmethod' })}</div>
                {authMethods.map(am => {
                  const sel = formAuthMethod === am.value;
                  return (
                    <div key={am.value} style={S.modCard(sel)} onClick={() => this.setState({ formAuthMethod: am.value })}>
                      <div style={{ width: 34, height: 34, borderRadius: 8, background: '#eef2ff', color: '#4f46e5', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><KeyRound size={18} /></div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: 14, color: '#0f172a' }}>{am.label}</div>
                        <div style={{ fontSize: 12.5, color: '#64748b', lineHeight: 1.35 }}>{am.desc}</div>
                      </div>
                      <div style={{ width: 18, height: 18, borderRadius: '50%', border: `2px solid ${sel ? GREEN : '#cbd5e1'}`, background: sel ? GREEN : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{sel && <Check size={12} color="#fff" />}</div>
                    </div>
                  );
                })}
              </>
            )}

            {groups.map(g => (
              <div key={g}>
                <div style={S.sectionLabel}>{groupLabel[g] || g}</div>
                {visibleFields.filter(f => (f.group || 'credentials') === g).map(f => {
                  const isSecret = f.secret || f.type === 'password'; const show = secretVisible[f.key];
                  return (
                    <div key={f.key}>
                      <TextField fullWidth margin="dense" size="small" type={isSecret && !show ? 'password' : 'text'}
                        label={f.label + (f.required ? ' *' : '')} value={formValues[f.key] ?? ''}
                        onChange={(ev) => this.onFieldChange(f.key, ev.target.value)} InputLabelProps={{ shrink: true }}
                        placeholder={f.default || ''} autoComplete="off"
                        InputProps={isSecret ? { endAdornment: (<InputAdornment position="end"><IconButton size="small" onClick={() => this.toggleSecret(f.key)} edge="end">{show ? <EyeOff size={16} /> : <Eye size={16} />}</IconButton></InputAdornment>) } : undefined} />
                      {isSecret && editingConfig && <div style={S.hint}>{intl.formatMessage({ id: 'voip.secret.keep' })}</div>}
                    </div>
                  );
                })}
              </div>
            ))}

            {isWebhookProvider && (
              <div style={{ marginTop: 8 }}>
                <div style={S.sectionLabel}>{intl.formatMessage({ id: 'voip.webhook.title' })}</div>
                <div style={{ fontSize: 12.5, color: '#64748b', marginBottom: 8 }}>{intl.formatMessage({ id: 'voip.webhook.desc' })}</div>
                {!editingConfig ? (
                  <div style={{ fontSize: 12.5, color: '#b45309' }}>{intl.formatMessage({ id: 'voip.webhook.savefirst' })}</div>
                ) : webhookUrl ? (
                  <>
                    <div style={{ background: '#1a1a2e', color: '#e0e0e0', fontFamily: 'monospace', fontSize: 12.5, padding: '10px 12px', borderRadius: 8, wordBreak: 'break-all', display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ flex: 1 }}>{webhookUrl}</span>
                      <Tooltip title={intl.formatMessage({ id: 'voip.copy' })}>
                        <IconButton size="small" onClick={() => this.copyWebhookUrl(webhookUrl)} sx={{ color: 'var(--mexx-green-primary, #8BC34A)' }}><Copy size={16} /></IconButton>
                      </Tooltip>
                    </div>
                    <Button size="small" variant="text" startIcon={<RefreshCw size={14} />} onClick={this.regenWebhook} style={{ color: '#334155', marginTop: 6 }}>
                      {intl.formatMessage({ id: 'voip.webhook.regenerate' })}
                    </Button>
                  </>
                ) : (
                  <Button size="small" variant="outlined" startIcon={<Link2 size={14} />} onClick={this.regenWebhook} style={{ borderColor: '#cbd5e1', color: '#334155' }}>
                    {intl.formatMessage({ id: 'voip.webhook.generate' })}
                  </Button>
                )}
              </div>
            )}

            <div style={S.sectionLabel}>{intl.formatMessage({ id: 'voip.test.title' })}</div>
            <div style={S.testBox}>
              <div style={{ padding: '10px 0' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ flex: 1, fontSize: 13.5, color: '#1f2937' }}>{intl.formatMessage({ id: 'voip.test.connection' })}</span>
                  <Button size="small" variant="outlined" disableElevation disabled={testConn.status === 'testing'} onClick={this.runTestConnection} style={{ borderColor: '#cbd5e1', color: '#334155', flexShrink: 0 }}>{intl.formatMessage({ id: 'voip.test.run' })}</Button>
                </div>
                <div style={{ marginTop: 6 }}>{this.renderTestStatus(testConn)}</div>
              </div>

              <div style={{ padding: '10px 0', borderTop: '1px solid #f1f3f5' }}>
                <div style={{ fontSize: 13.5, color: '#1f2937' }}>{intl.formatMessage({ id: 'voip.test.call' })}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
                  <div style={{ flex: 1, minWidth: 180 }}>
                    <PhoneInput country={'br'} value={testCallNumber} onChange={(v, c) => this.setState({ testCallNumber: v, testCallCountry: c })} countryCodeEditable={false} enableSearch inputStyle={{ width: '100%', height: 38 }} specialLabel="" />
                  </div>
                  <Button size="small" variant="contained" disableElevation startIcon={<PhoneCall size={15} />} disabled={['dialing', 'ringing', 'answered'].includes(testCall.status)} onClick={this.runTestCall} style={{ background: GREEN, flexShrink: 0 }}>{intl.formatMessage({ id: 'voip.test.call.run' })}</Button>
                </div>
                <FormControlLabel control={<Switch size="small" checked={testUseDdi} onChange={(e) => this.setState({ testUseDdi: e.target.checked })} />}
                  label={<span style={{ fontSize: 12.5, color: '#64748b' }}>{intl.formatMessage({ id: 'voip.use.ddi' })}{testCallCountry && testCallCountry.dialCode ? ` (+${testCallCountry.dialCode})` : ''}</span>} style={{ marginTop: 2 }} />
                <div style={{ marginTop: 6 }}>{this.renderTestStatus(testCall)}</div>
              </div>

              <div style={{ padding: '10px 0', borderTop: '1px solid #f1f3f5' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ flex: 1, fontSize: 13.5, color: '#1f2937' }}>{intl.formatMessage({ id: 'voip.test.receive' })}</span>
                  <Button size="small" variant="outlined" disableElevation startIcon={<Phone size={15} />} disabled={testReceive.status === 'listening'} onClick={this.runTestReceive} style={{ borderColor: '#cbd5e1', color: '#334155', flexShrink: 0 }}>{intl.formatMessage({ id: 'voip.test.receive.run' })}</Button>
                </div>
                <div style={{ marginTop: 6 }}>{this.renderTestStatus(testReceive)}</div>
              </div>
            </div>
          </div>
        </DialogContent>
        <DialogActions style={{ padding: '12px 20px' }}>
          <Button onClick={this.closeConfigDialog} style={{ color: '#64748b' }}>{intl.formatMessage({ id: 'cancel' })}</Button>
          <Button variant="contained" disableElevation style={{ background: GREEN }} onClick={this.saveConfig}>{intl.formatMessage({ id: 'save' })}</Button>
        </DialogActions>
      </Dialog>
    );
  }

  renderExtensionsModal() {
    const intl = this.props.intl;
    const { extModalOpen, extModalProvider, extModalConfig, extModalExtensions, extLoading, extSearch, extPage } = this.state;
    if (!extModalConfig) return null;

    const term = extSearch.trim().toLowerCase();
    const named = (e) => e.userName || `#${e.userId}`;
    const filtered = term ? extModalExtensions.filter(e => named(e).toLowerCase().includes(term) || String(e.extension).toLowerCase().includes(term)) : extModalExtensions;
    const pages = Math.max(1, Math.ceil(filtered.length / EXT_PAGE_SIZE));
    const page = Math.min(extPage, pages);
    const slice = filtered.slice((page - 1) * EXT_PAGE_SIZE, page * EXT_PAGE_SIZE);

    return (
      <Dialog open={extModalOpen} onClose={(e, reason) => { if (reason !== 'backdropClick') this.closeExtModal(); }} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 2, minHeight: 480 } }}>
        <DialogTitle style={{ display: 'flex', alignItems: 'center', gap: 12, paddingBottom: 10 }}>
          <span style={{ ...S.logo, width: 36, height: 36, fontSize: 14, background: logoColor(extModalProvider?.name) }}>{initial(extModalProvider?.displayName)}</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 17, fontWeight: 700 }}>{intl.formatMessage({ id: 'voip.extensions' })} — {extModalProvider?.displayName}</div>
            <div style={{ fontSize: 12.5, color: '#94a3b8', fontWeight: 400 }}>{intl.formatMessage({ id: 'voip.extensions.subtitle' })}</div>
          </div>
          <Button variant="contained" size="small" disableElevation startIcon={<Plus size={15} />} style={{ background: GREEN }} onClick={() => this.openExtDialog()}>{intl.formatMessage({ id: 'voip.add.extension' })}</Button>
        </DialogTitle>
        <DialogContent dividers>
          <TextField fullWidth size="small" margin="dense" placeholder={intl.formatMessage({ id: 'voip.search.ext' })}
            value={extSearch} onChange={(ev) => this.setState({ extSearch: ev.target.value, extPage: 1 })}
            InputProps={{ startAdornment: (<InputAdornment position="start"><Search size={16} color="#94a3b8" /></InputAdornment>) }} />

          <div style={{ marginTop: 8 }}>
            {extLoading ? (
              <div style={{ textAlign: 'center', padding: '40px 0' }}><CircularProgress size={28} color="secondary" /></div>
            ) : filtered.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px 0', color: '#94a3b8' }}>
                <Users size={40} color="#cbd5e1" />
                <div style={{ fontSize: 14, marginTop: 8 }}>{intl.formatMessage({ id: term ? 'voip.extensions.none.search' : 'voip.no.extensions' })}</div>
              </div>
            ) : slice.map(e => (
              <div key={e.id} style={S.extRow}>
                <span style={S.avatar}>{initial(named(e))}</span>
                <span style={{ flex: 1, fontSize: 13.5, color: '#1f2937' }}>{named(e)}</span>
                <span style={{ fontSize: 12.5, fontWeight: 600, color: '#475569', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, padding: '2px 9px' }}>{intl.formatMessage({ id: 'voip.extension.number' })} {e.extension}</span>
                {e.hasSipPassword && <Tooltip title={intl.formatMessage({ id: 'voip.sip.set' })}><Check size={15} color={GREEN} /></Tooltip>}
                <Tooltip title={intl.formatMessage({ id: 'voip.call' })}><IconButton size="small" style={{ color: GREEN }} onClick={() => this.openCallDialog(e)}><Phone size={14} /></IconButton></Tooltip>
                <IconButton size="small" onClick={() => this.openExtDialog(e)}><Pencil size={14} /></IconButton>
                <IconButton size="small" color="error" onClick={() => this.deleteExtension(e)}><Trash2 size={14} /></IconButton>
              </div>
            ))}
          </div>
        </DialogContent>
        <DialogActions style={{ padding: '10px 20px' }}>
          <span style={{ fontSize: 12.5, color: '#94a3b8' }}>{filtered.length} {intl.formatMessage({ id: 'voip.extensions' }).toLowerCase()}</span>
          <span style={{ flex: 1 }} />
          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <IconButton size="small" disabled={page <= 1} onClick={() => this.setState({ extPage: page - 1 })}><ChevronLeft size={18} /></IconButton>
            <span style={{ fontSize: 13, color: '#475569' }}>{page} / {pages}</span>
            <IconButton size="small" disabled={page >= pages} onClick={() => this.setState({ extPage: page + 1 })}><ChevronRight size={18} /></IconButton>
          </span>
          <Button onClick={this.closeExtModal} style={{ color: '#64748b', marginLeft: 8 }}>{intl.formatMessage({ id: 'cancel' })}</Button>
        </DialogActions>
      </Dialog>
    );
  }

  renderExtDialog() {
    const intl = this.props.intl;
    const { extDialogOpen, editingExt, extForm, technicians, techLoading, extModalExtensions } = this.state;
    const usedIds = extModalExtensions.filter(e => !editingExt || e.id !== editingExt.id).map(e => e.userId);
    const available = technicians.filter(t => !usedIds.includes(t.id));
    const selectedTech = technicians.find(t => t.id === extForm.userId) || null;

    return (
      <Dialog open={extDialogOpen} onClose={(e, reason) => { if (reason !== 'backdropClick') this.closeExtDialog(); }} fullWidth maxWidth="xs" PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle style={{ fontSize: 17, fontWeight: 700 }}>{intl.formatMessage({ id: editingExt ? 'voip.edit.extension' : 'voip.add.extension' })}</DialogTitle>
        <DialogContent dividers>
          {editingExt ? (
            <TextField fullWidth margin="dense" size="small" label={intl.formatMessage({ id: 'voip.agent' })} value={editingExt.userName || `#${editingExt.userId}`} disabled InputLabelProps={{ shrink: true }} />
          ) : (
            <Autocomplete options={available} loading={techLoading} value={selectedTech}
              getOptionLabel={(o) => o?.name || ''} isOptionEqualToValue={(o, v) => o.id === v.id}
              onChange={(ev, val) => this.onExtFieldChange('userId', val ? val.id : '')}
              noOptionsText={techLoading ? intl.formatMessage({ id: 'voip.test.testing' }) : intl.formatMessage({ id: 'voip.extensions.none.search' })}
              renderInput={(params) => (
                <TextField {...params} margin="dense" size="small" label={intl.formatMessage({ id: 'voip.agent' })} InputLabelProps={{ shrink: true }}
                  InputProps={{ ...params.InputProps, endAdornment: (<>{techLoading ? <CircularProgress size={16} /> : null}{params.InputProps.endAdornment}</>) }} />
              )} />
          )}
          <TextField fullWidth margin="dense" size="small" label={intl.formatMessage({ id: 'voip.extension.number' })}
            value={extForm.extension} onChange={(ev) => this.onExtFieldChange('extension', ev.target.value)} InputLabelProps={{ shrink: true }} />
          <TextField fullWidth margin="dense" size="small" type="password" label={intl.formatMessage({ id: 'voip.sip.password' })}
            value={extForm.sipPassword} onChange={(ev) => this.onExtFieldChange('sipPassword', ev.target.value)} InputLabelProps={{ shrink: true }} autoComplete="new-password" />
          {editingExt && <div style={S.hint}>{intl.formatMessage({ id: 'voip.secret.keep' })}</div>}
        </DialogContent>
        <DialogActions style={{ padding: '12px 20px' }}>
          <Button onClick={this.closeExtDialog} style={{ color: '#64748b' }}>{intl.formatMessage({ id: 'cancel' })}</Button>
          <Button variant="contained" disableElevation style={{ background: GREEN }} onClick={this.saveExtension} disabled={techLoading}>{intl.formatMessage({ id: 'save' })}</Button>
        </DialogActions>
      </Dialog>
    );
  }

  renderCallDialog() {
    const intl = this.props.intl;
    const { callDialogOpen, callExtension, callNumber, callCountry, callUseDdi, callStatus } = this.state;
    if (!callExtension) return null;
    return (
      <Dialog open={callDialogOpen} onClose={(e, reason) => { if (reason !== 'backdropClick') this.closeCallDialog(); }} fullWidth maxWidth="xs" PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle style={{ fontSize: 17, fontWeight: 700 }}>{intl.formatMessage({ id: 'voip.call' })}</DialogTitle>
        <DialogContent dividers>
          <div style={{ fontSize: 13, color: '#64748b', marginBottom: 10 }}>
            {intl.formatMessage({ id: 'voip.call.from' })}: <strong>{callExtension.userName || `#${callExtension.userId}`}</strong> · {intl.formatMessage({ id: 'voip.extension.number' })} {callExtension.extension}
          </div>
          <PhoneInput country={'br'} value={callNumber} onChange={(v, c) => this.setState({ callNumber: v, callCountry: c })} countryCodeEditable={false} enableSearch inputStyle={{ width: '100%', height: 42 }} specialLabel="" />
          <FormControlLabel control={<Switch size="small" checked={callUseDdi} onChange={(e) => this.setState({ callUseDdi: e.target.checked })} />}
            label={<span style={{ fontSize: 12.5, color: '#64748b' }}>{intl.formatMessage({ id: 'voip.use.ddi' })}{callCountry && callCountry.dialCode ? ` (+${callCountry.dialCode})` : ''}</span>} style={{ marginTop: 4 }} />
          <div style={{ marginTop: 8 }}>{this.renderTestStatus(callStatus)}</div>
        </DialogContent>
        <DialogActions style={{ padding: '12px 20px' }}>
          <Button onClick={this.closeCallDialog} style={{ color: '#64748b' }}>{intl.formatMessage({ id: 'cancel' })}</Button>
          <Button variant="contained" disableElevation startIcon={<Phone size={16} />} style={{ background: GREEN }} disabled={callStatus.status === 'dialing'} onClick={this.runCall}>{intl.formatMessage({ id: 'voip.call' })}</Button>
        </DialogActions>
      </Dialog>
    );
  }

  render() {
    const { classes, headerMenu } = this.props;
    const intl = this.props.intl;
    const { loading, actionLoading, providers } = this.state;

    return (
      <div className={classes.root} style={{ paddingLeft: 0, marginLeft: 0 }}>
        <HeaderMenu />
        <main className={clsx(classes.content, { [classes.contentShift]: headerMenu.open })}>
          <div className={classes.drawerHeader} />
          <Container component="main" maxWidth="xl">
            <h1 style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 2 }}>
              <PhoneCall size={24} color={GREEN} /> {intl.formatMessage({ id: 'voip.platforms.title' })}
            </h1>
            <p style={{ color: '#64748b', marginTop: 0 }}>{intl.formatMessage({ id: 'voip.platforms.subtitle' })}</p>

            {loading ? (
              <div style={S.grid}>
                {[1, 2, 3].map(i => (
                  <div key={i} style={{ ...S.card, padding: 18 }}>
                    <div style={{ display: 'flex', gap: 14 }}>
                      <Skeleton variant="rounded" width={48} height={48} animation="wave" />
                      <div style={{ flex: 1 }}><Skeleton variant="text" width={130} height={24} animation="wave" /><Skeleton variant="text" width="70%" animation="wave" /></div>
                    </div>
                    <Skeleton variant="text" width="90%" animation="wave" style={{ marginTop: 14 }} />
                    <Skeleton variant="text" width="80%" animation="wave" />
                    <Skeleton variant="rounded" width="100%" height={34} animation="wave" style={{ marginTop: 14 }} />
                  </div>
                ))}
              </div>
            ) : providers.length === 0 ? (
              <div style={S.empty}>
                <Phone size={56} color="#cbd5e1" />
                <div style={{ fontSize: 18, fontWeight: 600, marginTop: 8 }}>{intl.formatMessage({ id: 'voip.empty.title' })}</div>
                <div style={{ fontSize: 14 }}>{intl.formatMessage({ id: 'voip.empty.desc' })}</div>
              </div>
            ) : (
              <div style={S.grid}>{providers.map(p => this.renderProviderCard(p))}</div>
            )}

            {!loading && providers.length > 0 && (
              <VoipCallsHistory history={this.props.history} lang={this.props.match && this.props.match.params ? this.props.match.params.lang : 'pt'} />
            )}

            {this.renderConfigDialog()}
            {this.renderExtensionsModal()}
            {this.renderExtDialog()}
            {this.renderCallDialog()}
            {actionLoading && <Overlay><CircularProgress color="secondary" /></Overlay>}
            <CustomizedSnackbars variant={this.state.notificationVariant} message={this.state.notificationMessage} isOpen={this.state.openNotification} toClose={this.closeNotification} />
          </Container>
          <Footer />
        </main>
      </div>
    );
  }
}

const mapStateToProps = state => ({ headerMenu: state.headerMenu, actionsHeaderMenu: state.actionsHeaderMenu, userSession: state.userSession });
const mapDispatchToProps = dispatch => bindActionCreators(componentActions, dispatch);
export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(withStyles(Styles)(VoipPlatformList)));
